import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import SectionBadge from "@/components/site/SectionBadge";
import ContextualCTA from "@/components/site/v2/ContextualCTA";
import BookConsultationDialog from "@/components/site/BookConsultationDialog";
import JoinUsDialog from "@/components/site/JoinUsDialog";
import FloatingActions from "@/components/site/FloatingActions";
import { usePageMeta } from "@/lib/usePageMeta";

const GRID = "max-w-7xl mx-auto px-6 sm:px-8 md:px-12";

const FLOW = [
  {
    step: "01",
    title: "Complimentary discovery",
    body: "An initial conversation about the business problem, fit, urgency and available data. This is not a free full diagnostic, financial model or advisory report.",
    take: "You leave with a sharper statement of the problem, whether or not we work together.",
  },
  {
    step: "02",
    title: "Proposal and scope",
    body: "BLEZZINGS recommends an appropriate focused diagnostic or other engagement, with scope, timeline, responsibilities, fees, deliverables and acceptance criteria agreed before work starts.",
    take: "A decision document you can interrogate, not a deck you have to decode.",
  },
  {
    step: "03",
    title: "Evidence-led diagnostic and recommendations",
    body: "Once agreed, the consulting team tests commercial hypotheses, analyzes relevant data, prioritizes opportunities and presents an action plan within the statement of work.",
    take: "Findings you can act on — with us or without us.",
  },
  {
    step: "04",
    title: "Activation and optimization",
    body: "Where commissioned: we work with your teams and approved specialists to implement improvements, track measures and transfer capabilities.",
    take: "Results your team can sustain after we leave, not dependency on us staying.",
  },
];

const STARTS = [
  { id: "growth360", name: "Growth360", body: "Business growth diagnostic and opportunity prioritization — for stalled or unfocused growth." },
  { id: "profitshield", name: "ProfitShield", body: "Commercial profit leakage and margin recovery — when revenue rises but contribution thins." },
  { id: "salesmax", name: "SalesMax", body: "Sales productivity and execution excellence — coverage, conversion, cadence and incentives." },
  { id: "channelmax", name: "ChannelMax", body: "Distribution and channel performance — partner economics, coverage and sell-through." },
  { id: "commercemax", name: "CommerceMax", body: "Profitable e-commerce and marketplace growth — order economics, acquisition and retention." },
];

const WORKING = [
  {
    title: "Senior-led, accountable delivery",
    body: "BLEZZINGS leadership stays involved in critical decisions, recommendations and quality reviews. Trained analysts and approved domain specialists support the work as the engagement requires, under clear oversight.",
  },
  {
    title: "Evidence before recommendation",
    body: "We diagnose before we prescribe. Every recommendation traces back to your numbers, your customers and your market — not to a generic playbook.",
  },
  {
    title: "Your team is part of the work",
    body: "Expect to be involved: access to headline data, time with the people who own the problem, and honest answers in both directions. The work lands better when your team helps build it.",
  },
  {
    title: "Capability stays when we leave",
    body: "Playbooks, scorecards and rhythms are built to be run by your organisation, not to require ours. Success is measured by what continues after the engagement ends.",
  },
];

const PREP = [
  "Your headline numbers — revenue, growth, and where margin or conversion has moved",
  "What you have already tried, and what it taught you",
  "The decision you are actually facing — not the presentation version of it",
  "Who owns the problem internally, and how soon they can be in the room",
];

export default function HowWeEngagePage() {
  const [bookOpen, setBookOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);

  usePageMeta({
    title: "How We Engage — Working With BLEZZINGS Growth Partners",
    description:
      "How an engagement with BLEZZINGS Growth Partners works: discovery conversation, diagnostic, scoped proposal, implementation — and the five most common ways clients start.",
    canonical: "https://blezzings.co/how-we-engage",
    breadcrumbs: [
      { name: "Home", url: "https://blezzings.co/" },
      { name: "How We Engage", url: "https://blezzings.co/how-we-engage" },
    ],
  });

  const openBook = (source) => setBookOpen(true);

  return (
    <div data-testid="how-we-engage-page" className="relative">
      <Nav onBook={() => openBook("nav")} />

      {/* Hero */}
      <section className="pt-32 md:pt-40 pb-14 md:pb-20 bg-[#fafafa]">
        <div className={`${GRID} max-w-4xl`}>
          <SectionBadge label="How We Engage" />
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05]">
            From first conversation to measurable change.
          </h1>
          <p className="mt-6 text-lg md:text-xl text-zinc-600 leading-relaxed max-w-2xl">
            Every engagement follows the same honest sequence: understand before
            advising, diagnose before proposing, and build capability your team
            keeps. Here is how working with BLEZZINGS actually unfolds.
          </p>
        </div>
      </section>

      {/* The flow */}
      <section className="py-14 md:py-20 bg-white" data-testid="hwe-flow">
        <div className={GRID}>
          <div className="overline">The Sequence</div>
          <h2 className="font-serif-display text-2xl md:text-3xl mt-3">Four steps, no surprises</h2>
          <div className="mt-10 grid md:grid-cols-2 gap-5">
            {FLOW.map((f) => (
              <div key={f.step} className="border border-zinc-200 bg-white p-6 md:p-8" data-testid={`hwe-step-${f.step}`}>
                <div className="flex items-baseline gap-4">
                  <span className="font-serif-display text-3xl text-zinc-300">{f.step}</span>
                  <h3 className="font-serif-display text-xl md:text-2xl text-black">{f.title}</h3>
                </div>
                <p className="mt-4 text-[15px] text-zinc-600 leading-relaxed">{f.body}</p>
                <p className="mt-4 border-l-2 border-black pl-4 text-sm text-zinc-800 leading-relaxed">
                  <span className="font-medium">What you take away: </span>{f.take}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ways to start */}
      <section className="py-14 md:py-20 bg-[#fafafa]" data-testid="hwe-starts">
        <div className={GRID}>
          <div className="overline">Ways to Start</div>
          <h2 className="font-serif-display text-2xl md:text-3xl mt-3">The five doors most clients walk through</h2>
          <p className="mt-4 text-[15px] text-zinc-600 leading-relaxed max-w-3xl">
            These are starting points, not fixed packages — each is scoped to your
            situation after the discovery conversation. For broader mandates —
            new-market entry, brand and marketing transformation — explore{" "}
            <Link to="/capabilities/strategy-commercial-transformation" className="text-black font-medium underline underline-offset-4 decoration-zinc-300 hover:decoration-black transition-colors">our four practices</Link>.
          </p>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {STARTS.map((s, i) => (
              <Link
                key={s.id}
                to={`/solutions/${s.id}`}
                data-testid={`hwe-start-${i}`}
                className="group border border-zinc-200 bg-white p-6 hover:border-black transition-colors"
              >
                <h3 className="font-serif-display text-lg text-black leading-snug">{s.name}</h3>
                <p className="mt-3 text-sm text-zinc-600 leading-relaxed">{s.body}</p>
                <span className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-black">
                  Explore solution
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </Link>
            ))}
            <button
              type="button"
              onClick={() => openBook("how-we-engage-starts")}
              data-testid="hwe-start-cta"
              className="group border-2 border-black bg-black text-white p-6 text-left hover:bg-zinc-800 transition-colors"
            >
              <h3 className="font-serif-display text-lg leading-snug">Not sure which fits?</h3>
              <p className="mt-3 text-sm text-white/70 leading-relaxed">
                Describe the challenge in your own words — we will tell you plainly whether and how we can help.
              </p>
              <span className="mt-4 inline-flex items-center gap-2 text-sm font-medium">
                Start the conversation
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* Working together */}
      <section className="py-14 md:py-20 bg-white" data-testid="hwe-working">
        <div className={GRID}>
          <div className="overline">Working Together</div>
          <h2 className="font-serif-display text-2xl md:text-3xl mt-3">What to expect of us — and of yourselves</h2>
          <div className="mt-10 grid sm:grid-cols-2 gap-5 max-w-5xl">
            {WORKING.map((w) => (
              <div key={w.title} className="border-t-2 border-black pt-5">
                <h3 className="text-[15px] font-semibold text-black">{w.title}</h3>
                <p className="mt-2 text-sm text-zinc-600 leading-relaxed">{w.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Preparing for the first conversation */}
      <section className="py-14 md:py-20 bg-[#fafafa]" data-testid="hwe-prep">
        <div className={`${GRID} max-w-4xl`}>
          <div className="overline">Before We Meet</div>
          <h2 className="font-serif-display text-2xl md:text-3xl mt-3">What makes the first conversation useful</h2>
          <p className="mt-4 text-[15px] text-zinc-600 leading-relaxed max-w-2xl">
            No preparation is required — but these four things turn an introductory
            call into a working session:
          </p>
          <ul className="mt-8 space-y-4">
            {PREP.map((p, i) => (
              <li key={i} className="flex gap-4 border-b border-black/10 pb-4" data-testid={`hwe-prep-${i}`}>
                <span className="text-xs text-zinc-400 tabular pt-1">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-[15px] text-zinc-700 leading-relaxed">{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <ContextualCTA
        testid="hwe-cta"
        heading="Bring us the problem as it actually is."
        body="The first conversation costs nothing but an honest hour. If we are not the right firm for it, we will say so — and tell you who might be."
        label="Discuss Your Growth Challenge"
        onBook={() => openBook("how-we-engage")}
      />

      <Footer onJoin={() => setJoinOpen(true)} />
      <FloatingActions onBook={() => openBook("floating")} />
      <BookConsultationDialog open={bookOpen} onOpenChange={setBookOpen} service="Growth Strategy" />
      <JoinUsDialog open={joinOpen} onOpenChange={setJoinOpen} />
    </div>
  );
}
